import { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box, Tabs, Tab } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import IndividualDiscountCodesPanel from '../DiscountCodes/IndividualDiscountCodesPanel';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function DiscountCodeManagement() {
  const authCtx = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);

  if (!authCtx.checkRight('discountCode:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const discountCodeSchema = {
    code: {
      label: 'Code',
      api: { itemField: 'code' },
      placeholder: 'SUMMER24',
      type: 'text',
      required: true,
      minWidth: '160px',
    },
    description: {
      label: 'Beschreibung',
      api: { itemField: 'description' },
      placeholder: 'Optionale Beschreibung',
      type: 'textarea',
      required: false,
      minWidth: '240px',
    },
    userHint: {
      label: 'Kundenhinweis',
      api: { itemField: 'userHint' },
      placeholder: 'Wird dem Kunden bei Eingabe des Codes angezeigt',
      type: 'textarea',
      required: false,
      minWidth: '240px',
    },
    active: {
      label: 'Aktiv',
      api: { itemField: 'active' },
      type: 'boolean',
      required: false,
      minWidth: '120px',
    },
    globalPercent: {
      label: 'Rabatt % (global)',
      api: { itemField: 'globalPercent' },
      placeholder: 'z. B. 10',
      type: 'number',
      required: true,
      minWidth: '140px',
    },
    percentOverrides: {
      label: 'Overrides (Filiale/Aktivität)',
      type: 'nested',
      addButtonText: 'Override hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'percentOverrides',
        nestedEndpoint: 'percentOverride',
      },
      schema: {
        branchId: {
          label: 'Filiale',
          type: 'select',
          api: {
            resource: 'branch',
            idName: '_id',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          required: true,
          transformValue: v => (typeof v === 'object' ? v._id || v.value || v.id : v),
        },
        entertainmentId: {
          label: 'Entertainment (optional)',
          type: 'select',
          api: {
            resource: 'entertainment',
            idName: '_id',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          required: false,
          transformValue: v => (typeof v === 'object' ? v._id || v.value || v.id : v),
        },
        percent: {
          label: 'Prozent',
          type: 'number',
          required: true,
        },
      },
    },
    allowedBranches: {
      label: 'Erlaubte Filialen',
      type: 'multi-select',
      api: { resource: 'branch', labelField: 'longName', valueField: '_id', skipSpecial: true },
      required: false,
      placeholder: 'Filialen auswählen',
      transformValue: (selectedValues /* ids or option values */) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array.map(v => (typeof v === 'object' ? v._id || v.value || v.id : v));
      },
    },
    allowedEntertainments: {
      label: 'Erlaubte Entertainments',
      type: 'multi-select',
      api: {
        resource: 'entertainment',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      placeholder: 'Entertainments auswählen',
      transformValue: (selectedValues /* ids or option values */) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array.map(v => (typeof v === 'object' ? v._id || v.value || v.id : v));
      },
    },
    allowedLineItemTypes: {
      label: 'Erlaubte Positionstypen',
      type: 'multi-select',
      options: [
        { value: 'booking', label: 'Buchung' },
        { value: 'package', label: 'Paket' },
        { value: 'upsell', label: 'Upsell' },
      ],
      required: false,
    },
    allowedProductIds: {
      label: 'Erlaubte Produkte',
      type: 'multi-select',
      api: {
        resource: 'product',
        idName: '_id',
        labelField: 'internalName',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
    },
    allowedPackageIds: {
      label: 'Erlaubte Pakete',
      type: 'multi-select',
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      placeholder: 'Pakete auswählen (leer = alle Pakete)',
      // Shown only when the discount is restricted to package line items.
      showIf: item =>
        Array.isArray(item?.allowedLineItemTypes) && item.allowedLineItemTypes.includes('package'),
    },
    allowedUpsellIds: {
      label: 'Erlaubte Upsells',
      type: 'multi-select',
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      placeholder: 'Upsells auswählen (leer = alle Upsells)',
      // Shown only when the discount is restricted to upsell line items.
      showIf: item =>
        Array.isArray(item?.allowedLineItemTypes) && item.allowedLineItemTypes.includes('upsell'),
    },
    allowedUpgradeIds: {
      label: 'Erlaubte Upgrades (kombiniert, veraltet)',
      type: 'multi-select',
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      // Legacy combined list applied to both packages and upsells. Only shown for
      // existing codes that still use it; new codes should use the per-type
      // "Erlaubte Pakete" / "Erlaubte Upsells" fields above. The backend falls
      // back to this list when the matching per-type list is empty.
      showIf: item => Array.isArray(item?.allowedUpgradeIds) && item.allowedUpgradeIds.length > 0,
    },
    redeemValidFrom: {
      label: 'Einlösbar ab',
      api: { itemField: 'redeemValidFrom' },
      type: 'datetime-local',
      required: false,
      minWidth: '200px',
    },
    redeemValidTo: {
      label: 'Einlösbar bis',
      api: { itemField: 'redeemValidTo' },
      type: 'datetime-local',
      required: false,
      minWidth: '200px',
    },
    eventValidFrom: {
      label: 'Eventdatum ab',
      api: { itemField: 'eventValidFrom' },
      type: 'date',
      required: false,
      minWidth: '160px',
    },
    eventValidTo: {
      label: 'Eventdatum bis',
      api: { itemField: 'eventValidTo' },
      type: 'date',
      required: false,
      minWidth: '160px',
    },
    usageLimit: {
      label: 'Max. Einlösungen',
      api: { itemField: 'usageLimit' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    perEmailLimit: {
      label: 'Max. je E-Mail',
      api: { itemField: 'perEmailLimit' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    forceOnlinePayment: {
      label: 'Online-Zahlung erzwingen',
      api: { itemField: 'forceOnlinePayment' },
      type: 'boolean',
      required: false,
      minWidth: '200px',
    },
    maxParticipants: {
      label: 'Max. Teilnehmerzahl',
      api: { itemField: 'maxParticipants' },
      placeholder: 'z. B. 6',
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    bookingComment: {
      label: 'Buchungskommentar',
      api: { itemField: 'bookingComment' },
      placeholder: 'Wird bei Einlösung an den Buchungskommentar angehängt',
      type: 'textarea',
      required: false,
      minWidth: '240px',
    },
    availabilityWindows: {
      label: 'Verfügbarkeitsfenster (Eventzeit)',
      type: 'nested',
      addButtonText: 'Zeitfenster hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'availabilityWindows',
        nestedEndpoint: 'availabilityWindow',
      },
      schema: {
        dayCode: {
          label: 'Wochentage',
          type: 'multi-select',
          options: [
            { value: 0, label: 'Sonntag' },
            { value: 1, label: 'Montag' },
            { value: 2, label: 'Dienstag' },
            { value: 3, label: 'Mittwoch' },
            { value: 4, label: 'Donnerstag' },
            { value: 5, label: 'Freitag' },
            { value: 6, label: 'Samstag' },
          ],
          required: true,
        },
        start: {
          label: 'Von (HH:MM)',
          type: 'text',
          placeholder: '09:00',
          required: true,
        },
        end: {
          label: 'Bis (HH:MM)',
          type: 'text',
          placeholder: '18:00',
          required: true,
        },
      },
    },
    allowIndividualCodes: {
      label: 'Individuelle Codes: MA-Ziehung erlauben',
      api: { itemField: 'allowIndividualCodes' },
      type: 'boolean',
      required: false,
    },
    allowIndividualCodeLists: {
      label: 'Individuelle Codes: Partner-Liste erlauben',
      api: { itemField: 'allowIndividualCodeLists' },
      type: 'boolean',
      required: false,
    },
    individualCodeExpiryDays: {
      label: 'Code-Gültigkeit (Tage)',
      api: { itemField: 'individualCodeExpiryDays' },
      placeholder: 'z. B. 30',
      type: 'number',
      required: false,
    },
    individualCodePrefix: {
      label: 'Code-Präfix (leer = Rabattcode)',
      api: { itemField: 'individualCodePrefix' },
      placeholder: 'z. B. funzonecb',
      type: 'text',
      required: false,
      // Prefix of generated individual codes; alphanumeric only, max 16 chars,
      // prefix + random suffix never exceed 20 chars in total.
      showIf: item =>
        item?.allowIndividualCodes === true || item?.allowIndividualCodeLists === true,
    },
    individualCodeSuffixLength: {
      label: 'Zufallszeichen (4–16)',
      api: { itemField: 'individualCodeSuffixLength' },
      placeholder: 'z. B. 9',
      type: 'number',
      required: false,
      showIf: item =>
        item?.allowIndividualCodes === true || item?.allowIndividualCodeLists === true,
    },
    drawLimit: {
      label: 'Ziehungslimit (pro MA)',
      api: { itemField: 'drawLimit' },
      placeholder: 'z. B. 2',
      type: 'number',
      required: false,
    },
    drawLimitPeriod: {
      label: 'Ziehungsperiode',
      api: { itemField: 'drawLimitPeriod' },
      type: 'select',
      options: [
        { value: 'week', label: 'Woche' },
        { value: 'month', label: 'Monat' },
        { value: 'quarter', label: 'Quartal' },
        { value: 'year', label: 'Jahr' },
      ],
      required: false,
    },
    drawAllowedRoles: {
      label: 'Ziehung: Erlaubte Rollen',
      type: 'multi-select',
      api: {
        resource: 'role',
        labelField: 'readableName',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      placeholder: 'Rollen auswählen (leer = kein MA-Zugang)',
      transformValue: selectedValues => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array.map(v => (typeof v === 'object' ? v._id || v.value || v.id : v));
      },
    },
    drawAllowedBranches: {
      label: 'Ziehung: Erlaubte Filialen',
      type: 'multi-select',
      api: {
        resource: 'branch',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
      },
      required: false,
      placeholder: 'Filialen auswählen (leer = kein Filial-Filter)',
      transformValue: selectedValues => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array.map(v => (typeof v === 'object' ? v._id || v.value || v.id : v));
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label='Rabattcodes' />
        <Tab label='Individuelle Codes' />
      </Tabs>
      {activeTab === 0 && (
        <EditableList
          schema={discountCodeSchema}
          title='Neuen Rabattcode anlegen'
          resourceType='discountCode'
          searchPlaceholder='Rabattcodes durchsuchen'
          parentIsEditable={authCtx.checkRight('discountCode:update')}
          options={{ titleKey: 'code' }}
          visibleColumnKeys={['code', 'description', 'active', 'globalPercent']}
        />
      )}
      {activeTab === 1 && <IndividualDiscountCodesPanel />}
    </Box>
  );
}

export default DiscountCodeManagement;
